import React, { useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, Stack, Typography } from "@mui/material";
import moment from "moment";
import { useParams } from "react-router-dom";
import { useGetLastUpdatedOnStatusQuery } from "store/slices/apis/projectStatusApiSlice";
import SchedulePanel from "./SchedulePanel";
import { enterpriseTokens, overviewChartPalette } from "styles/theme";


const LegendItem = ({ color, label, dashed = false }) => (
    <Stack direction="row" spacing={1} alignItems="center">
        <Box
            sx={{
                width: '20px',
                height: '3px',
                borderRadius: 999,
                bgcolor: color,
                ...(dashed && {
                    background: `repeating-linear-gradient(
                    to right,
                    ${color},
                    ${color} 6px,
                    transparent 6px,
                    transparent 10px
                )`,
                }),
            }}
        />
        <Typography
            variant="body1"
            sx={{
                fontSize: '12px',
                fontWeight: 700,
                color: enterpriseTokens.slate,
                letterSpacing: '1%',
                fontFamily: "Inter, Outfit, sans-serif",
            }}
        >
            {label}
        </Typography>
    </Stack>
);

const SCurveSchedule = ({ kpiData, scheduleData, isCurveSuccess }) => {
    // console.log(kpiData?.data);
    const { projectId } = useParams()
    console.log("data", scheduleData);
    const { data: scheduleDate } = useGetLastUpdatedOnStatusQuery({ ProjectId: projectId });
    const row = scheduleData?.data?.[0];
    console.log("row", row, kpiData);
    const plannedProgress = row?.planProgress || 0;
    const actualProgress = row?.actualProgress || 0;

    const categories = kpiData?.data?.map(item => moment(item?.month)?.format('MMM-YY')) || [];
    // const statusLabel = moment(scheduleDate?.scheduleUpdatedDate).format("MMM-YY");
    const statusLabel = scheduleDate?.scheduleUpdatedDate
        ? moment(scheduleDate.scheduleUpdatedDate).format("MMM-YY")
        : null;
    // const statusIndex = categories.findIndex((c) => c === statusLabel);
    //console.log("statusLabel", statusLabel);
    const statusIndex = statusLabel
        ? categories.findIndex((c) => c === statusLabel)
        : -1;

    const rows = kpiData?.data || [];
    const plan = kpiData?.data.map(item => item?.plan);
    const actual = kpiData?.data.map(item => item?.actual);
    const projected = kpiData?.data.map(item => item?.eV_Cum_WithForecast);

    const statusRow =
        statusIndex >= 0 && statusIndex < rows.length ? rows[statusIndex] : null;

    console.log("plannedProgress",);
    console.log("actualProgress", actualProgress);

    const actualColor =
        plannedProgress != null && actualProgress != null
            ? (plannedProgress <= actualProgress ? overviewChartPalette.teal : overviewChartPalette.coral)
            : overviewChartPalette.coral;
    console.log("actualColor", actualColor);

    //const { month, plan, actual, forecast } = kpiData?.data;

    const progress = localStorage.getItem('progress') || 0;

    const options = useMemo(
        () => ({
            chart: {
                type: "spline",
                backgroundColor: "transparent",
                height: 452,
                marginLeft: 70,
                spacingTop: 16,
                spacingRight: 20,
            },

            title: {
                text: "",
            },
            subtitle: {
                text: "",
            },
            credits: { enabled: false },

            xAxis: {
                categories,
                tickLength: 0,
                lineColor: enterpriseTokens.lineSoft,
                labels: {
                    style: {
                        fontSize: "12px",
                        color: enterpriseTokens.slate,
                        fontWeight: 700,
                        fontFamily: "Inter, Outfit, sans-serif",
                    },
                    formatter: function () {
                        if (this.pos === statusIndex) return `<span style="font-weight:800;color:${enterpriseTokens.navy}">${this.value}</span>`;
                        return this.value;
                    },
                    useHTML: true,
                },
                title: {
                    text: "Months",
                    align: "middle",
                    style: {
                        color: enterpriseTokens.muted,
                        fontSize: "13px",
                        fontWeight: 700,
                        fontFamily: "Inter, Outfit, sans-serif",
                    },
                    margin: 20,
                },
                plotLines:
                    statusIndex >= 0
                        ? [
                            {
                                value: statusIndex,
                                color: enterpriseTokens.muted,
                                width: 1,
                                dashStyle: "Dot",
                                zIndex: 5,
                                label: {
                                    text: "Status Date",
                                    rotation: 0,
                                    y: -10,
                                    style: {
                                        color: enterpriseTokens.muted,
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        fontFamily: "Inter, Outfit, sans-serif",
                                    },
                                },
                            },
                        ]
                        : [],
            },

            yAxis: {
                title: {
                    text: "% Progress",
                    style: {
                        color: enterpriseTokens.muted,
                        fontSize: "13px",
                        fontWeight: 700,
                        fontFamily: "Inter, Outfit, sans-serif",
                    },
                },
                gridLineColor: "#EEF2F7",
                labels: {
                    enabled: true,
                    style: {
                        fontSize: "12px",
                        color: enterpriseTokens.slate,
                        fontWeight: 700,
                        fontFamily: "Inter, Outfit, sans-serif",
                    },
                },
                min: 0,
                max: 100
            },

            tooltip: {
                shared: true,
                useHTML: true,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                borderColor: enterpriseTokens.lineSoft,
                shadow: true,
                padding: 12,
                style: {
                    fontSize: "12px",
                    fontFamily: "Inter, Outfit, sans-serif",
                    color: enterpriseTokens.navy,
                },
                formatter: function () {
                    const points = this.points || [];
                    return `
                        <div style="font-family:Inter, Outfit, sans-serif; color:${enterpriseTokens.navy}; line-height:1.45;">
                            ${points.map((point) => `
                                <div style="display:flex; align-items:center; gap:8px; white-space:nowrap; margin:2px 0;">
                                    <span style="color:${point.color}; font-size:14px; line-height:1;">●</span>
                                    <span>${point.series.name}: <b>${Number(point.y).toFixed(2)} %</b></span>
                                </div>
                            `).join("")}
                        </div>
                    `;
                },
            },

            plotOptions: {
                series: {
                    marker: {
                        enabled: true,
                        radius: 4,
                        lineWidth: 1,
                    },
                },
                spline: {
                    lineWidth: 3,
                },
            },
            legend: {
                enabled: false
            },
            series: [
                {
                    name: "Planned Value",
                    data: plan,
                    color: overviewChartPalette.indigo,
                    marker: {
                        symbol: "circle",
                        fillColor: overviewChartPalette.indigo,
                    },
                    dataLabels: {
                        enabled: true,
                        formatter: function () {
                            return this.point.index === statusIndex
                                ? `${this.y.toFixed(2)} %`
                                : null;
                        },
                        style: {
                            fontWeight: 'bold',
                            color: enterpriseTokens.navy,
                            textOutline: "none"
                        }
                    },
                },
                {
                    name: "Actual Value",
                    data: actual,
                    color: actualColor,
                    marker: {
                        symbol: "circle",
                        fillColor: actualColor,
                    },
                    dataLabels: {
                        enabled: true,
                        formatter: function () {
                            return this.point.index === statusIndex
                                ? `${this.y.toFixed(2)} %`
                                : null;
                        },
                        style: {
                            fontWeight: 'bold',
                            color: '#000'
                        }
                    },
                },
                {
                    name: "Projected",
                    data: projected,
                    color: overviewChartPalette.violet,
                    dashStyle: "ShortDash",
                    marker: {
                        symbol: "circle",
                        fillColor: overviewChartPalette.violet,
                    },
                },

            ],
        }), [statusIndex]
    );

    return ({ isCurveSuccess } &&
        <SchedulePanel
            eyebrow="Progress trend"
            title="Delivery Progress Curve"
            subtitle="See whether delivered progress is closing or widening the gap against plan."
            legend={
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }}>
                    <LegendItem color={overviewChartPalette.indigo} label="Planned" />
                    <LegendItem color={actualColor} label="Delivered" />
                    <LegendItem color={overviewChartPalette.violet} label="Forecast" dashed />
                </Stack>
            }
            bodySx={{ pt: 1, pb: 2 }}
        >
            <HighchartsReact
                highcharts={Highcharts}
                options={options} />
        </SchedulePanel>
    );
};

export default SCurveSchedule;
